import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, NgControl } from '@angular/forms';
import { IonDatetime, ModalController } from '@ionic/angular';
import dayjs from 'dayjs';
import { cloneDeep, parseInt } from 'lodash';
import type { MaskitoOptions } from '@maskito/core';
import { maskitoDateOptionsGenerator } from '@maskito/kit';

const STANDARD_MIN_DATE = new Date('1900-01-01');
const STANDARD_DATE_FORMAT = 'dd.MM.yyyy';
const STANDARD_MIN_STRING = '1900-01-01';

@Component({
  selector: 'pia-date-picker',
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.scss'],
})
export class DateComponent implements OnInit {
  @Input() public title = '';
  @Input() public placeholder = '';
  @Input() public min = STANDARD_MIN_DATE;
  // If no max is provided it will be set to 50 years from now.
  @Input() public max = dayjs().add(50, 'year').toDate();
  @Input() public preSelection: Date;
  @Input() public required = false;
  @Input() public icon: string;
  @Input() public disabled: boolean;
  @Input() public noIcon = false;
  @Input() public description: string;
  @Input() public dateFormatter = STANDARD_DATE_FORMAT;
  @Input() public noDeleteButton = false;
  @Input() formControl: AbstractControl;
  @Input() freeTextInput = false;

  @Output() public change: EventEmitter<Date> = new EventEmitter<Date>();

  uniqueTriggerId = '';
  initialInputDate: Date;
  changedDate = false;
  date: Date;
  dateString: string = null;
  minString = STANDARD_MIN_STRING;
  maxString: string;

  private onChange = (_date: Date) => {};
  private onTouched = () => {};

  mask: MaskitoOptions;

  constructor(
    public ngControl: NgControl,
    private changeDetectorRef: ChangeDetectorRef,
    private modalCtrl: ModalController
  ) {
    ngControl.valueAccessor = this;
  }

  ngOnInit() {
    if (this.freeTextInput) {
      this.mask = maskitoDateOptionsGenerator({
        mode: 'dd/mm/yyyy',
        separator: '.',
        min: this.min,
        max: this.max,
      });
    } else {
      this.uniqueTriggerId = 'trigger-' + Math.random().toString(36).substring(2, 9);
    }
    // ion-datetime does not seem to provide an independent preselection Date so the date gets set to the actual passed on value or today

    if (this.date instanceof Date) {
      this.dateString = this.returnDateStringWithoutTime(this.date);
    }

    if (this.min instanceof Date) {
      this.minString = this.returnDateStringWithoutTime(this.min);
    }

    if (this.max instanceof Date) {
      this.maxString = this.returnDateStringWithoutTime(this.max);
    }
  }

  onDateChange() {
    const isValidDate = this.isValidDate(this.createDateFromString(this.dateString));

    if (isValidDate) {
      // prevents unwanted change events on parent component!
      if (!this.isSameDate(this.date, this.createDateFromString(this.dateString))) {
        if (this.changedDate || this.freeTextInput) {
          this.date = this.createDateFromString(this.dateString);
          if (this.date < this.min) {
            this.date = this.min;
          }
          this.change.emit(this.date);
          this.onChange(this.date);
          this.onTouched();
        }
      }
    }
  }

  public markAsTouched(): void {
    this.onTouched();
  }

  public updateChanges() {
    this.onChange(this.date);
  }

  // ControlValueAccessor interface
  public writeValue(value: any) {
    this.initialInputDate = cloneDeep(value);
    // On purpose to return false on empty strings, null, undefined, 0, ...
    if (this.isValidDate(value)) {
      this.date = value;
      this.dateString = this.returnDateStringWithoutTime(this.date);
      this.changeDetectorRef.markForCheck();
    }
  }

  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  clearDate(event: Event): void {
    event.stopPropagation();
    this.date = null;
    this.dateString = null;
    this.change.emit(null);
  }

  public removeInput($event): void {
    this.clearDate($event);
    this.updateChanges();
  }

  private isSameDate(a: Date, b: Date): boolean {
    // prevents false positives / crashes if one of both is not a valid date
    if (!this.isValidDate(a)) {
      return false;
    }
    if (!this.isValidDate(b)) {
      return false;
    }
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private isValidDate(date) {
    return date instanceof Date && !Number.isNaN(date.getTime());
  }

  async cancel(datePicker: IonDatetime) {
    if (!this.changedDate) {
      this.date = this.initialInputDate;
    }
    datePicker.cancel();
    (await this.modalCtrl.getTop()).dismiss();
  }

  async confirm(datePicker: IonDatetime) {
    this.changedDate = true;
    datePicker.confirm();
    (await this.modalCtrl.getTop()).dismiss();
  }

  private returnDateStringWithoutTime(date: Date): string {
    const dateString = date.toISOString().split('T')[0];
    if (this.freeTextInput) {
      if (!dateString) {
        return '';
      }
      const [year, month, day] = dateString.split('-');
      return `${day}.${month}.${year}`;
    } else {
      return dateString;
    }
  }

  private createDateFromString(inputDateString: string) {
    if (this.freeTextInput) {
      const [day, month, year] = inputDateString.split('.');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      return new Date(inputDateString);
    }
  }
}
